export interface HelpStep {
  title: string;
  description: string;
}

export interface PageHelp {
  title: string;
  description: string;
  steps: HelpStep[];
}

const helpContent: Record<string, PageHelp> = {
  '/dashboard': {
    title: 'Dashboard Ejecutivo',
    description: 'Panel principal con indicadores clave de ventas y rendimiento del negocio.',
    steps: [
      {
        title: 'KPIs principales',
        description: 'Muestra las ventas del período, cantidad de tickets emitidos y ticket promedio, con su variación respecto al período anterior.'
      },
      {
        title: 'Filtrar por período',
        description: 'Usa los selectores de "Fecha Desde" y "Fecha Hasta" para ver datos de un rango de fechas específico.'
      },
      {
        title: 'Filtrar por local',
        description: 'Selecciona "Todos los Locales" o un local específico para ver métricas filtradas por sucursal.'
      },
      {
        title: 'Ventas por hora',
        description: 'El gráfico de líneas muestra la distribución de ventas por hora del día, ayudando a identificar las horas pico.'
      },
      {
        title: 'Ventas por sucursal',
        description: 'El gráfico de barras compara los ingresos entre las diferentes sucursales.'
      }
    ]
  },

  '/pos': {
    title: 'Punto de Venta (POS)',
    description: 'Pantalla principal para registrar ventas y procesar cobros.',
    steps: [
      {
        title: 'Buscar productos',
        description: 'Escribe el nombre del producto en la barra de búsqueda o filtra por categoría para encontrarlo rápidamente.'
      },
      {
        title: 'Agregar al carrito',
        description: 'Haz clic en un producto para agregarlo. Si tiene múltiples presentaciones (Simple, Doble, etc.), selecciona la que deseas.'
      },
      {
        title: 'Complementos y consideraciones',
        description: 'Al agregar un producto puedes seleccionar complementos adicionales y consideraciones especiales (ej: Sin azúcar, Caliente).'
      },
      {
        title: 'Modificar cantidades',
        description: 'En el carrito puedes aumentar o disminuir la cantidad de cada producto, o eliminarlo.'
      },
      {
        title: 'Procesar cobro',
        description: 'Haz clic en "Cobrar" para abrir la ventana de pago. Selecciona el método de pago (efectivo, tarjeta, Yape, etc.) y el tipo de comprobante (boleta o factura).'
      },
      {
        title: 'Para llevar / En local',
        description: 'Selecciona si el pedido es para consumir en el local o para llevar.'
      },
      {
        title: 'Pedidos de apps',
        description: 'La pestaña de pedidos de apps muestra las órdenes recibidas desde plataformas de delivery.'
      },
      {
        title: 'Estado de caja',
        description: 'El indicador de caja muestra si la caja está abierta o cerrada. Debes tener la caja abierta para procesar ventas.'
      }
    ]
  },

  '/inventory': {
    title: 'Auditoría de Inventario',
    description: 'Realiza conteos físicos de inventario y compara con el stock del sistema.',
    steps: [
      {
        title: 'Ver auditorías',
        description: 'La lista muestra todas las auditorías realizadas con su fecha, local, estado y discrepancias encontradas.'
      },
      {
        title: 'Crear auditoría',
        description: 'Haz clic en "+" para iniciar una nueva auditoría. Selecciona el local y la fecha del conteo.'
      },
      {
        title: 'Filtrar',
        description: 'Filtra las auditorías por local y rango de fechas para encontrar auditorías específicas.'
      },
      {
        title: 'Realizar conteo',
        description: 'Dentro de una auditoría, ingresa las cantidades contadas físicamente para cada insumo. El sistema calculará las diferencias automáticamente.'
      },
      {
        title: 'Finalizar auditoría',
        description: 'Al completar el conteo, finaliza la auditoría. Se mostrará un resumen con todas las discrepancias encontradas.'
      }
    ]
  },

  '/ingredients': {
    title: 'Gestión de Insumos',
    description: 'Administra el catálogo global de ingredientes y materias primas.',
    steps: [
      {
        title: 'Ver insumos',
        description: 'La tabla muestra todos los insumos con su nombre, categoría, unidad de medida, costo y estado. Usa la búsqueda y filtros para encontrar insumos específicos.'
      },
      {
        title: 'Crear insumo',
        description: 'Haz clic en "Nuevo Insumo" para agregar un ingrediente. Define su nombre, categoría, unidad de medida y costo por unidad.'
      },
      {
        title: 'Editar insumo',
        description: 'Haz clic en "Editar" para modificar los datos de un insumo existente.'
      },
      {
        title: 'Historial de precios',
        description: 'Consulta y registra cambios en el costo de compra de cada insumo para mantener un histórico de precios.'
      },
      {
        title: 'Stock por local',
        description: 'Gestiona el stock de cada insumo por sucursal. Ve las cantidades disponibles en cada local.'
      },
      {
        title: 'Proveedores',
        description: 'Asocia proveedores a cada insumo para saber a quién comprar cada ingrediente.'
      },
      {
        title: 'Activar/Desactivar',
        description: 'Desactiva insumos que ya no se utilizan. Los insumos inactivos no aparecen en recetas ni conteos.'
      }
    ]
  },

  '/purchase-orders': {
    title: 'Órdenes de Compra',
    description: 'Gestiona las solicitudes de compra de insumos a proveedores.',
    steps: [
      {
        title: 'Ver órdenes',
        description: 'La lista muestra todas las órdenes de compra con su número, proveedor, local destino, estado y monto total.'
      },
      {
        title: 'Crear orden',
        description: 'Haz clic en "Nueva Orden" para crear una solicitud de compra. Selecciona el proveedor, local destino y los insumos a comprar con sus cantidades.'
      },
      {
        title: 'Recibir orden',
        description: 'Cuando llega el pedido, haz clic en "Recibir" para registrar las cantidades realmente recibidas. El stock se actualiza automáticamente.'
      },
      {
        title: 'Filtrar',
        description: 'Filtra las órdenes por estado (pendiente, recibida, cancelada) y rango de fechas.'
      },
      {
        title: 'Ver detalle',
        description: 'Haz clic en una orden para ver todos los productos solicitados, cantidades y precios.'
      }
    ]
  },

  '/cash-register': {
    title: 'Gestión de Caja',
    description: 'Controla la apertura, cierre y movimientos de caja por turno.',
    steps: [
      {
        title: 'Abrir caja',
        description: 'Al inicio del turno, abre la caja ingresando el monto inicial (fondo de caja). Esto habilita el registro de ventas.'
      },
      {
        title: 'Resumen diario',
        description: 'La pestaña "Resumen" muestra todas las ventas del turno con sus montos y métodos de pago.'
      },
      {
        title: 'Registrar gastos',
        description: 'En la pestaña "Gastos" puedes registrar salidas de efectivo por compras menores, pagos a proveedores u otros gastos operativos.'
      },
      {
        title: 'Conteo de efectivo',
        description: 'En "Conteo de Caja" ingresa la cantidad de billetes y monedas contados físicamente. El sistema calcula el total y lo compara con lo esperado.'
      },
      {
        title: 'Balance final',
        description: 'La pestaña "Balance" muestra el resumen del turno: ventas, gastos, monto esperado vs contado, y la diferencia (sobrante o faltante).'
      },
      {
        title: 'Cerrar caja',
        description: 'Al finalizar el turno, cierra la caja. Se generará un resumen completo del día. Una vez cerrada no se pueden registrar más ventas.'
      }
    ]
  },

  '/products': {
    title: 'Gestión de Productos',
    description: 'Administra el catálogo completo de productos del menú.',
    steps: [
      {
        title: 'Ver productos',
        description: 'La tabla muestra todos los productos registrados con su nombre, categoría, presentaciones y estado. Usa los filtros de búsqueda y categoría para encontrar productos específicos.'
      },
      {
        title: 'Crear producto',
        description: 'Haz clic en "Nuevo Producto" para agregar un producto al catálogo. Completa el nombre, categoría y opcionalmente una descripción.'
      },
      {
        title: 'Editar producto',
        description: 'Haz clic en el ícono de editar (lápiz) en la fila del producto para modificar sus datos.'
      },
      {
        title: 'Gestionar presentaciones',
        description: 'Cada producto puede tener múltiples presentaciones (ej: Simple, Doble, Combo). Haz clic en "Presentaciones" para agregar o quitar presentaciones y asignar precios a cada una.'
      },
      {
        title: 'Gestionar recetas',
        description: 'Haz clic en "Recetas" para definir qué insumos se consumen al vender cada presentación del producto. Esto permite el descuento automático del inventario.'
      },
      {
        title: 'Asignar locales',
        description: 'Haz clic en "Locales" para definir en qué sucursales está disponible el producto.'
      },
      {
        title: 'Activar/Desactivar',
        description: 'Puedes desactivar un producto sin eliminarlo. Los productos inactivos no aparecen en el POS.'
      }
    ]
  },

  '/reports': {
    title: 'Reportes y Análisis',
    description: 'Consulta reportes detallados de ventas, inventario y operaciones.',
    steps: [
      {
        title: 'Seleccionar categoría',
        description: 'Elige entre Ventas, Inventario u Operaciones para acceder a los diferentes tipos de reportes disponibles.'
      },
      {
        title: 'Reportes de ventas',
        description: 'Incluye: reporte general de ventas, productos más vendidos, modalidades de pago y detalle de ventas con filtros por fecha, turno y local.'
      },
      {
        title: 'Reportes de inventario',
        description: 'Incluye: consumo de insumos, stock actual, diferencias entre pedido y recepción, y errores de inventario.'
      },
      {
        title: 'Reportes de operaciones',
        description: 'Incluye: reporte de caja por turno y eficiencia de producción (transformaciones).'
      },
      {
        title: 'Filtros',
        description: 'Cada reporte tiene filtros específicos: local, rango de fechas, turno, categoría, etc. Ajústalos para obtener la información que necesitas.'
      }
    ]
  },

  '/customers': {
    title: 'Gestión de Clientes',
    description: 'Administra la información de los clientes del negocio.',
    steps: [
      {
        title: 'Ver clientes',
        description: 'La tabla muestra todos los clientes con su nombre, documento, teléfono y estado. Usa la búsqueda para encontrar clientes específicos.'
      },
      {
        title: 'Crear cliente',
        description: 'Haz clic en "Nuevo Cliente" para registrar un cliente. Ingresa nombre, tipo y número de documento, teléfono y dirección.'
      },
      {
        title: 'Editar cliente',
        description: 'Haz clic en "Editar" para modificar los datos de un cliente existente.'
      },
      {
        title: 'Ver detalles',
        description: 'Consulta el historial de compras y estadísticas de cada cliente.'
      },
      {
        title: 'Activar/Desactivar',
        description: 'Puedes desactivar clientes sin eliminarlos. Los clientes inactivos no aparecen en el POS al buscar por documento.'
      }
    ]
  },

  '/promotions': {
    title: 'Promociones y Descuentos',
    description: 'Gestiona las ofertas especiales y descuentos del negocio.',
    steps: [
      {
        title: 'Dashboard de promociones',
        description: 'La pestaña "Dashboard" muestra un resumen de promociones activas, por vencer y métricas generales.'
      },
      {
        title: 'Ver promociones',
        description: 'La pestaña "Promociones" lista todas las ofertas con su nombre, tipo de descuento, vigencia y estado.'
      },
      {
        title: 'Crear promoción',
        description: 'Haz clic en "Nuevo Descuento" para crear una promoción. Define el nombre, tipo (porcentaje o monto fijo), productos aplicables, días y horarios vigentes.'
      },
      {
        title: 'Editar promoción',
        description: 'Haz clic en "Editar" para modificar los datos de una promoción existente.'
      },
      {
        title: 'Activar/Desactivar',
        description: 'Activa o desactiva promociones según la temporada. Solo las promociones activas se aplican automáticamente en el POS.'
      }
    ]
  },

  '/employees': {
    title: 'Gestión de Empleados',
    description: 'Administra la información, permisos y accesos de tu equipo de trabajo.',
    steps: [
      {
        title: 'Ver empleados',
        description: 'La tabla muestra todos los empleados con su nombre, cargo, email, local asignado y estado. Filtra por cargo o local.'
      },
      {
        title: 'Crear empleado',
        description: 'Haz clic en "Nuevo Empleado" para registrar un colaborador. Ingresa datos personales, cargo, email y contraseña de acceso.'
      },
      {
        title: 'Editar empleado',
        description: 'Haz clic en "Editar" para modificar los datos de un empleado.'
      },
      {
        title: 'Asignar sucursales',
        description: 'En la sección de permisos, selecciona a qué sucursales tiene acceso el empleado. Esto determina a qué locales puede ingresar al hacer login.'
      },
      {
        title: 'Permisos del sistema',
        description: 'Asigna permisos específicos para controlar qué módulos puede ver y usar cada empleado (POS, inventario, reportes, etc.).'
      },
      {
        title: 'Activar/Desactivar',
        description: 'Desactiva empleados que ya no laboran. No podrán iniciar sesión pero su historial se conserva.'
      }
    ]
  },

  '/distribution': {
    title: 'Centro de Distribución',
    description: 'Gestiona la distribución inteligente de insumos entre sucursales.',
    steps: [
      {
        title: 'Sugerencias automáticas',
        description: 'El sistema analiza el stock actual y consumo de cada local para sugerir qué insumos transferir y en qué cantidades.'
      },
      {
        title: 'Generar sugerencias',
        description: 'Haz clic en "Generar Sugerencias" para que el sistema calcule las transferencias recomendadas basándose en los niveles de stock mínimo configurados.'
      },
      {
        title: 'Ver transferencias',
        description: 'La pestaña "Transferencias" muestra el historial de todas las transferencias realizadas entre locales con su estado.'
      },
      {
        title: 'Transferencia manual',
        description: 'En "Transferencia Manual" puedes crear una transferencia seleccionando origen, destino y los insumos con sus cantidades.'
      },
      {
        title: 'Seguimiento',
        description: 'Cada transferencia pasa por estados: solicitada, aprobada, en tránsito, recibida. Puedes ver el detalle de cada una.'
      }
    ]
  },

  '/locations': {
    title: 'Gestión de Sucursales',
    description: 'Administra la información de las sucursales del negocio.',
    steps: [
      {
        title: 'Ver sucursales',
        description: 'La lista muestra todas las sucursales con su nombre, dirección, tipo (principal o sucursal), horarios y estado.'
      },
      {
        title: 'Crear sucursal',
        description: 'Haz clic en "+" para agregar una nueva sucursal. Define nombre, dirección, teléfono, gerente y horarios de atención.'
      },
      {
        title: 'Editar sucursal',
        description: 'Modifica los datos de una sucursal existente incluyendo horarios, configuración de pago y series de comprobantes.'
      },
      {
        title: 'Configurar turnos',
        description: 'Define los horarios de cada turno (mañana/tarde) por día de la semana para cada sucursal.'
      },
      {
        title: 'Activar/Desactivar',
        description: 'Desactiva sucursales que no están operativas temporalmente.'
      }
    ]
  },

  '/kardex': {
    title: 'Kardex de Insumos',
    description: 'Consulta el historial completo de movimientos de inventario por insumo.',
    steps: [
      {
        title: 'Seleccionar insumo',
        description: 'Busca y selecciona el insumo del que deseas ver los movimientos.'
      },
      {
        title: 'Seleccionar local',
        description: 'Elige la sucursal para ver los movimientos de stock de ese insumo en ese local específico.'
      },
      {
        title: 'Filtrar por fechas',
        description: 'Ajusta el rango de fechas para ver movimientos de un período específico.'
      },
      {
        title: 'Ver movimientos',
        description: 'El reporte muestra cada entrada y salida del insumo con fecha, tipo de movimiento (compra, venta, transferencia, ajuste), cantidad y saldo resultante.'
      }
    ]
  },

  '/transformations': {
    title: 'Órdenes de Transformación',
    description: 'Gestiona la conversión de insumos en productos (ej: harina → pan).',
    steps: [
      {
        title: 'Ver órdenes',
        description: 'La lista muestra todas las órdenes de transformación con su regla, estado, fecha e insumos involucrados.'
      },
      {
        title: 'Crear transformación',
        description: 'Haz clic en "Crear Transformación" para iniciar una orden. Selecciona la regla de transformación, local y las cantidades a procesar.'
      },
      {
        title: 'Completar orden',
        description: 'Cuando la transformación se completa, registra las cantidades reales obtenidas. El sistema descontará los insumos de origen y sumará los productos resultantes.'
      },
      {
        title: 'Filtrar',
        description: 'Filtra por local, ingrediente origen/destino, rango de fechas y estado de la orden.'
      },
      {
        title: 'Cancelar orden',
        description: 'Puedes cancelar una orden de transformación pendiente si ya no es necesaria.'
      }
    ]
  },

  '/suppliers': {
    title: 'Gestión de Proveedores',
    description: 'Administra la información de los proveedores de insumos.',
    steps: [
      {
        title: 'Ver proveedores',
        description: 'La tabla muestra todos los proveedores con su nombre, RUC, contacto, ciudad y estado.'
      },
      {
        title: 'Crear proveedor',
        description: 'Haz clic en "Nuevo Proveedor" para registrar un proveedor. Ingresa razón social, RUC, dirección, teléfono y persona de contacto.'
      },
      {
        title: 'Editar proveedor',
        description: 'Modifica los datos de contacto y comerciales de un proveedor existente.'
      },
      {
        title: 'Asociar insumos',
        description: 'Vincula los insumos que cada proveedor suministra para facilitar la creación de órdenes de compra.'
      },
      {
        title: 'Activar/Desactivar',
        description: 'Desactiva proveedores con los que ya no trabajas. No aparecerán al crear órdenes de compra.'
      }
    ]
  },

  '/vouchers': {
    title: 'Gestión de Comprobantes',
    description: 'Busca, consulta y anula comprobantes electrónicos emitidos.',
    steps: [
      {
        title: 'Buscar comprobantes',
        description: 'Usa los filtros de fecha para buscar boletas y facturas emitidas en un período específico.'
      },
      {
        title: 'Ver detalle',
        description: 'Haz clic en un comprobante para ver su detalle completo: productos, montos, datos del cliente y estado SUNAT.'
      },
      {
        title: 'Anular comprobante',
        description: 'Para anular un comprobante, haz clic en "Anular" e ingresa el motivo. Se generará una nota de crédito automáticamente.'
      },
      {
        title: 'Comprobante manual',
        description: 'Haz clic en "Nuevo Comprobante" para crear un comprobante manual (boleta o factura) fuera del flujo de venta.'
      }
    ]
  },

  '/accounts-payable': {
    title: 'Cuentas por Pagar',
    description: 'Gestiona las obligaciones de pago con proveedores.',
    steps: [
      {
        title: 'Ver cuentas',
        description: 'La lista muestra todas las cuentas por pagar con su proveedor, monto, fecha de vencimiento y estado de pago.'
      },
      {
        title: 'Filtrar por estado',
        description: 'Filtra las cuentas por estado: pendientes, parcialmente pagadas, pagadas o vencidas para priorizar los pagos.'
      },
      {
        title: 'Registrar pago',
        description: 'Haz clic en "Pagar" para registrar un pago parcial o total contra una cuenta. Ingresa el monto y método de pago.'
      },
      {
        title: 'Ver historial',
        description: 'Consulta el detalle de todos los pagos realizados contra cada cuenta.'
      },
      {
        title: 'Cuentas vencidas',
        description: 'Las cuentas vencidas se resaltan en rojo. Revísalas periódicamente para evitar retrasos con proveedores.'
      }
    ]
  },

  '/merchandise-movements': {
    title: 'Movimientos de Mercadería',
    description: 'Registra entradas y salidas de mercadería fuera del flujo normal de compras y ventas.',
    steps: [
      {
        title: 'Ver movimientos',
        description: 'La lista muestra todos los movimientos con su tipo (entrada/salida), motivo, estado y fecha. Filtra por dirección y estado.'
      },
      {
        title: 'Crear movimiento',
        description: 'Haz clic en "Nuevo Movimiento" para registrar una entrada o salida. Selecciona el tipo de movimiento, local, insumos y cantidades.'
      },
      {
        title: 'Tipos de movimiento',
        description: 'Entradas: donaciones, devoluciones, ajustes positivos. Salidas: merma, consumo interno, ajustes negativos.'
      },
      {
        title: 'Aprobación',
        description: 'Algunos movimientos requieren aprobación de un supervisor antes de afectar el stock.'
      },
      {
        title: 'Cancelar movimiento',
        description: 'Puedes cancelar un movimiento pendiente si fue registrado por error.'
      }
    ]
  },

  '/stock-config': {
    title: 'Configuración de Stock',
    description: 'Define los niveles mínimos de stock para órdenes de compra y transferencias.',
    steps: [
      {
        title: 'Órdenes de compra',
        description: 'La pestaña "Órdenes de Compra" muestra una matriz de insumos vs locales. Ingresa el stock mínimo que debe tener cada insumo en cada local para generar alertas de compra.'
      },
      {
        title: 'Transferencias',
        description: 'La pestaña "Transferencias" permite configurar el stock mínimo por insumo, local, día de la semana y turno para sugerir transferencias automáticas.'
      },
      {
        title: 'Editar valores',
        description: 'Haz clic en cualquier celda para ingresar o modificar el stock mínimo. Las celdas editadas se resaltan en amarillo (existentes) o verde (nuevas).'
      },
      {
        title: 'Grabar cambios',
        description: 'Haz clic en "Grabar" para guardar todos los cambios realizados. El contador muestra cuántos cambios tienes pendientes.'
      },
      {
        title: 'Filtrar',
        description: 'Usa la búsqueda por nombre de ingrediente y el filtro de categoría para encontrar insumos específicos.'
      }
    ]
  },

  '/system-parameters': {
    title: 'Parámetros del Sistema',
    description: 'Configura los datos maestros y parámetros generales del sistema.',
    steps: [
      {
        title: 'Seleccionar tipo',
        description: 'Usa las pestañas para navegar entre los diferentes tipos de parámetros: Fiscales, Conductores, Vehículos, Transporte, Cargos, Movimientos, Presentaciones, Categorías, Cat. Insumos y Unidades.'
      },
      {
        title: 'Parámetros fiscales',
        description: 'Configura los impuestos y cargos aplicables a las ventas (IGV, recargo al consumo, etc.).'
      },
      {
        title: 'Crear parámetro',
        description: 'En cada pestaña, haz clic en "Nuevo" para crear un registro. Completa los campos requeridos y guarda.'
      },
      {
        title: 'Editar parámetro',
        description: 'Haz clic en "Editar" en la fila del registro que deseas modificar.'
      },
      {
        title: 'Activar/Desactivar',
        description: 'Puedes activar o desactivar cualquier parámetro. Los parámetros inactivos no se muestran en los selectores del sistema.'
      }
    ]
  },

  '/bread-transfers': {
    title: 'Transferencias de Pan',
    description: 'Gestiona la distribución diaria de pan a las sucursales por turno.',
    steps: [
      {
        title: 'Seleccionar fecha',
        description: 'Elige la fecha para la que deseas registrar la distribución de pan.'
      },
      {
        title: 'Turno mañana',
        description: 'En la pestaña "Mañana" ingresa las cantidades de cada tipo de pan enviadas a cada sucursal en el turno de la mañana.'
      },
      {
        title: 'Turno tarde',
        description: 'En la pestaña "Tarde" registra la distribución del turno de la tarde de la misma manera.'
      },
      {
        title: 'Completar sesión',
        description: 'Al terminar de registrar todas las cantidades, completa la sesión para confirmar la distribución.'
      },
      {
        title: 'Pedidos',
        description: 'La tabla de pedidos muestra las cantidades solicitadas por cada sucursal como referencia.'
      }
    ]
  },

  '/discrepancy-review': {
    title: 'Revisión de Diferencias',
    description: 'Compara cantidades pedidas vs recibidas en transferencias y órdenes de compra.',
    steps: [
      {
        title: 'Filtrar búsqueda',
        description: 'Selecciona el local, categoría de insumo y rango de fechas para buscar las diferencias.'
      },
      {
        title: 'Buscar',
        description: 'Haz clic en "Buscar" para cargar las transferencias y órdenes de compra con diferencias en el período seleccionado.'
      },
      {
        title: 'Revisar diferencias',
        description: 'La tabla muestra los insumos donde la cantidad recibida difiere de la solicitada, con el porcentaje de diferencia y valor monetario.'
      },
      {
        title: 'Corregir cantidades',
        description: 'Si una diferencia fue un error de registro, puedes editar la cantidad confirmada directamente en la tabla.'
      },
      {
        title: 'Aplicar ajustes',
        description: 'Haz clic en "Aplicar" para confirmar las correcciones realizadas. Esto actualiza las cantidades en el sistema.'
      }
    ]
  },

  '/void-sales': {
    title: 'Anulación de Ventas',
    description: 'Consulta y gestiona las ventas anuladas del sistema.',
    steps: [
      {
        title: 'Ver ventas anuladas',
        description: 'La lista muestra todas las ventas que han sido anuladas con su motivo, fecha y monto.'
      },
      {
        title: 'Buscar',
        description: 'Usa los filtros de fecha y local para encontrar anulaciones específicas.'
      }
    ]
  }
};

/**
 * Obtiene el contenido de ayuda para una ruta.
 * Soporta rutas exactas y con parámetros (ej: /products/123 → /products)
 */
export const getHelpForPath = (pathname: string): PageHelp | null => {
  // Primero buscar match exacto
  if (helpContent[pathname]) {
    return helpContent[pathname];
  }

  // Buscar match parcial (para rutas con parámetros como /products/123)
  const basePath = '/' + pathname.split('/').filter(Boolean)[0];
  if (helpContent[basePath]) {
    return helpContent[basePath];
  }

  return null;
};

export default helpContent;
